import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useGetQueryFn from '../use-get-query-fn';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';

const BASE = '/api/admin/whatsapp/lines';
const SERVER = '/api/admin/whatsapp/evolution-server';

type Envelope<T> = { status: number; data: T };

export interface LineActionDef {
  action: string;
  method: 'GET' | 'POST' | 'DELETE';
  /** whatsapp.view | whatsapp.send | whatsapp.manage_numbers */
  perm: string;
  kind: 'read' | 'send' | 'write';
}

export type Obj = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

/** What the backend allows, with the permission per action. */
export const useLineActionsCatalogue = () => {
  const queryFn = useGetQueryFn<Envelope<LineActionDef[]>>({
    defaultErrorMessage: 'An unexpected error occurred while loading the line actions',
    endpoint: `${BASE}/actions`,
  });
  return useQuery({ queryKey: ['whatsapp-line-actions'], queryFn: () => queryFn(), staleTime: 5 * 60_000, retry: false });
};

export interface CallArgs {
  instance: string;
  action: string;
  body?: Obj;
  query?: Obj;
}

/** One call through the pass-through; resolves to Evolution's body. */
export const useLineActionCall = () => {
  const request = useClientStore((s) => s.request);
  const mutationFn = withErrorHandling(async ({ instance, action, body, query }: CallArgs) => {
    const res = await request<Envelope<any>>(`${BASE}/${instance}/call`, 'POST', { action, body, query }); // eslint-disable-line @typescript-eslint/no-explicit-any
    return res?.data;
  }, 'The WhatsApp action failed');
  return useMutation({ mutationFn });
};

/** A read action as a query. `body` is for POST reads (findChats...), `query` for GET reads. */
export function useLineRead<T = any>( // eslint-disable-line @typescript-eslint/no-explicit-any
  instance: string,
  action: string,
  opts: { body?: Obj; query?: Obj; enabled?: boolean; refetchInterval?: number } = {},
) {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: ['whatsapp-line-read', instance, action, opts.body ?? null, opts.query ?? null],
    queryFn: withErrorHandling(async (): Promise<T> => {
      const res = await request<Envelope<T>>(`${BASE}/${instance}/call`, 'POST', {
        action,
        body: opts.body,
        query: opts.query,
      });
      return res?.data;
    }, 'The WhatsApp request failed'),
    enabled: (opts.enabled ?? true) && !!instance,
    refetchInterval: opts.refetchInterval,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Invalidate every cached read of one action (after a write). */
export const useInvalidateLineRead = () => {
  const qc = useQueryClient();
  return (instance: string, action?: string) =>
    qc.invalidateQueries({ queryKey: action ? ['whatsapp-line-read', instance, action] : ['whatsapp-line-read', instance] });
};

// ---------------------------------------------------------------------------
// Evolution server controls

export interface ServerStatus {
  containers?: { service: string; state: string; status?: string; image?: string; digest?: string; started_at?: string }[];
  evolution?: { version?: string; [k: string]: unknown };
  host?: {
    uptime_s?: number;
    load?: number[] | number;
    mem_total_mb?: number;
    mem_avail_mb?: number;
    disk_total_gb?: number;
    disk_free_gb?: number;
    swap_used_mb?: number;
  };
  previous_image?: string | null;
}

export const SERVER_STATUS_KEY = ['whatsapp-evolution-server-status'];

export const useServerStatusQuery = (enabled: boolean) => {
  const queryFn = useGetQueryFn<Envelope<ServerStatus>>({
    defaultErrorMessage: 'An unexpected error occurred while loading the Evolution server status',
    endpoint: `${SERVER}/status`,
  });
  return useQuery({ queryKey: SERVER_STATUS_KEY, queryFn: () => queryFn(), enabled, retry: false, refetchOnWindowFocus: false });
};

export const useServerLogsQuery = (service: 'api' | 'redis', tail: number, enabled: boolean) => {
  const queryFn = useGetQueryFn<Envelope<{ lines: string[] }>>({
    defaultErrorMessage: 'An unexpected error occurred while loading the server logs',
    endpoint: `${SERVER}/logs`,
  });
  return useQuery({
    queryKey: ['whatsapp-evolution-server-logs', service, tail],
    queryFn: () => queryFn({ service, tail }),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
};

export const useServerOpMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const mutationFn = withErrorHandling(
    async ({ op, tag }: { op: 'restart' | 'upgrade' | 'rollback'; tag?: string }) =>
      request<Envelope<Obj>>(`${SERVER}/${op}`, 'POST', op === 'upgrade' ? { tag } : op === 'restart' ? { service: 'api' } : {}, { timeout: 240_000 }),
    'The server operation failed',
  );
  return useMutation({ mutationFn, onSettled: () => qc.invalidateQueries({ queryKey: SERVER_STATUS_KEY }) });
};
